import { Badge } from "@/components/ui/badge";
import { conversationLinkFor } from "@/lib/activityLabels";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  getIdeaStatusDescription,
  getIdeaStatusTone,
  mapIdeaToUiState,
} from "@/lib/ideaUi";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type {
  EditableIdeaFields,
  Idea,
} from "@/schemas/firestore/idea.schema";
import type { IdeaUiOverride } from "@/lib/ideaUi";
import { PencilIcon, XIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";

type IdeaEditDraft = Omit<EditableIdeaFields, "content_outline"> & {
  content_outline: string;
};

interface IdeaExpandedSheetProps {
  idea: Idea | null;
  ideaUiOverride?: IdeaUiOverride;
  isOpen: boolean;
  onClose: () => void;
  onCreateDraft?: (
    idea: Idea,
    wpType: "posts" | "pages",
    customInstructions?: string,
  ) => Promise<void> | void;
  onUpdateIdea?: (
    idea: Idea,
    ideaData: EditableIdeaFields,
  ) => Promise<void> | void;
  onOpenLinkedPage?: (idea: Idea) => void;
  isWordPressConnected?: boolean;
  isUpdatingIdea?: boolean;
}

function formatTimestamp(
  timestamp: { toDate?: () => Date } | Date | null | undefined,
): string | null {
  if (!timestamp) return null;

  try {
    const date =
      timestamp instanceof Date
        ? timestamp
        : typeof timestamp.toDate === "function"
          ? timestamp.toDate()
          : null;

    return date?.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }) ?? null;
  } catch {
    return null;
  }
}

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  if (!value) {
    return null;
  }

  return (
    <div className="grid gap-1">
      <div className="text-xs font-medium text-foreground">{label}</div>
      <div className="text-sm text-muted-foreground break-words">{value}</div>
    </div>
  );
}

/** DetailRow's sibling for the one row whose value is a destination. */
function DetailLinkRow({
  label,
  href,
  children,
}: {
  label: string;
  href: string | null;
  children: React.ReactNode;
}) {
  if (!href) {
    return null;
  }

  return (
    <div className="grid gap-1">
      <div className="text-xs font-medium text-foreground">{label}</div>
      <Link
        to={href}
        className="text-sm text-muted-foreground underline underline-offset-2 break-words hover:text-foreground"
      >
        {children}
      </Link>
    </div>
  );
}

function EditTextField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="grid gap-1">
      <span className="text-xs font-medium text-foreground">{label}</span>
      <Input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
      />
    </label>
  );
}

function EditTextareaField({
  label,
  value,
  onChange,
  placeholder,
  rows = 5,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <label className="grid gap-2">
      <span className="text-sm font-medium">{label}</span>
      <Textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        rows={rows}
      />
    </label>
  );
}

function createIdeaEditDraft(idea: Idea): IdeaEditDraft {
  return {
    title: idea.title,
    description: idea.description,
    target_keyword: idea.target_keyword,
    suggested_slug: idea.suggested_slug,
    rationale: idea.rationale,
    target_audience: idea.target_audience,
    content_outline: idea.content_outline.join("\n"),
    source_input: idea.source_input,
  };
}

function normalizeIdeaDraft(draft: IdeaEditDraft): EditableIdeaFields {
  return {
    title: draft.title.trim(),
    description: draft.description.trim(),
    target_keyword: draft.target_keyword.trim(),
    suggested_slug: draft.suggested_slug.trim().replace(/^\/+/, ""),
    rationale: draft.rationale.trim(),
    target_audience: draft.target_audience.trim(),
    content_outline: draft.content_outline
      .split("\n")
      .map((item) => item.trim())
      .filter(Boolean),
    source_input: draft.source_input.trim(),
  };
}

export function IdeaExpandedSheet({
  idea,
  ideaUiOverride,
  isOpen,
  onClose,
  onCreateDraft,
  onUpdateIdea,
  onOpenLinkedPage,
  isWordPressConnected = true,
  isUpdatingIdea = false,
}: IdeaExpandedSheetProps) {
  const [customInstructions, setCustomInstructions] = useState("");
  const [now, setNow] = useState(() => Date.now());
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<IdeaEditDraft | null>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (!isOpen || !ideaUiOverride?.isCreatingDraft) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [ideaUiOverride?.isCreatingDraft, isOpen]);

  if (!idea) {
    return null;
  }

  const activeDraft = draft ?? createIdeaEditDraft(idea);
  const ideaUiState = mapIdeaToUiState(idea, ideaUiOverride);
  const statusDescription = getIdeaStatusDescription(ideaUiState);
  const createdAt = formatTimestamp(idea.created_at);
  const updatedAt = formatTimestamp(idea.updated_at);
  const implementedAt = formatTimestamp(idea.implemented_at);
  const hasOutline = idea.content_outline.length > 0;
  const isReady = ideaUiState.kind === "ready";
  const isImplemented = ideaUiState.kind === "implemented";
  const isCreating = ideaUiState.kind === "creating";
  const lockRemainingMs =
    isCreating && ideaUiState.expiresAt
      ? Math.max(ideaUiState.expiresAt - now, 0)
      : 0;
  const lockRemainingMinutes = Math.floor(lockRemainingMs / 60000);
  const lockRemainingSeconds = Math.floor((lockRemainingMs % 60000) / 1000);
  const lockRemainingLabel = isCreating
    ? `${lockRemainingMinutes}:${String(lockRemainingSeconds).padStart(2, "0")}`
    : null;
  const displayTitle = isEditing
    ? activeDraft.title || "Untitled idea"
    : idea.title;
  const displayKeyword = isEditing
    ? activeDraft.target_keyword.trim()
    : idea.target_keyword;
  const displaySlug = isEditing
    ? activeDraft.suggested_slug.trim().replace(/^\/+/, "")
    : idea.suggested_slug;

  const updateDraft = <Field extends keyof IdeaEditDraft>(
    field: Field,
    value: IdeaEditDraft[Field],
  ) => {
    setDraft((current) => ({
      ...(current ?? createIdeaEditDraft(idea)),
      [field]: value,
    }));
  };

  const startEditing = () => {
    setDraft(createIdeaEditDraft(idea));
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setDraft(createIdeaEditDraft(idea));
    setIsEditing(false);
  };

  const saveDraft = async () => {
    if (!onUpdateIdea || isUpdatingIdea) {
      return;
    }

    try {
      await onUpdateIdea(idea, normalizeIdeaDraft(activeDraft));
      setIsEditing(false);
    } catch {
      // The parent owns save error messaging; keep the draft open for retry.
    }
  };

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          setCustomInstructions("");
          setIsEditing(false);
          setDraft(null);
          onClose();
        }
      }}
    >
      <SheetContent
        side="bottom"
        className="h-[90dvh] sm:max-w-full overflow-y-auto p-0"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          titleRef.current?.focus({ preventScroll: true });
        }}
      >
        <div className="flex h-full flex-col">
          <SheetHeader className="sticky top-0 z-10 border-b bg-background">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 space-y-2">
                <SheetTitle
                  ref={titleRef}
                  tabIndex={-1}
                  className="text-xl outline-none"
                >
                  {displayTitle}
                </SheetTitle>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge
                    variant="outline"
                    className={`text-xs py-0 px-1.5 ${getIdeaStatusTone(ideaUiState)}`}
                  >
                    {ideaUiState.label}
                  </Badge>
                  {displayKeyword && (
                    <Badge variant="outline">{displayKeyword}</Badge>
                  )}
                  {displaySlug && (
                    <Badge variant="secondary">/{displaySlug}</Badge>
                  )}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {isEditing ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={cancelEditing}
                      disabled={isUpdatingIdea}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => void saveDraft()}
                      disabled={isUpdatingIdea}
                    >
                      {isUpdatingIdea ? "Saving" : "Save"}
                    </Button>
                  </>
                ) : (
                  onUpdateIdea && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={startEditing}
                          aria-label="Edit idea"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Edit idea</TooltipContent>
                    </Tooltip>
                  )
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={onClose}
                  disabled={isUpdatingIdea}
                  aria-label="Close idea"
                >
                  <XIcon className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </SheetHeader>

          <div className="flex-1 space-y-6 p-6">
            {idea.error_message && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                <div className="text-sm font-medium text-red-800">
                  Generation Error
                </div>
                <p className="mt-2 text-sm text-red-700">{idea.error_message}</p>
              </div>
            )}

            <div className="rounded-lg border p-4 space-y-4">
              <div className="space-y-1">
                <div className="text-sm font-medium">WordPress Actions</div>
                <p className="text-sm text-muted-foreground">
                  {isReady &&
                    "Start an async WordPress draft from this idea. The idea will be updated after generation finishes."}
                  {isCreating &&
                    `Draft generation has started. This idea is locked to prevent duplicate drafts until the backend writes the final state${lockRemainingLabel ? ` or the fallback timer ends in ${lockRemainingLabel}` : ""}.`}
                  {isImplemented &&
                    "This idea already has a canonical linked page. Create actions are disabled."}
                  {ideaUiState.kind === "loading" &&
                    "This idea is still generating and cannot be used yet."}
                  {ideaUiState.kind === "archived" &&
                    "Archived ideas are kept for reference only."}
                  {ideaUiState.kind === "error" &&
                    "Failed ideas cannot be used for WordPress draft creation."}
                </p>
              </div>

              {/* Draft generation is a core write (min_role=editor); the parent
                  withholds the callback for viewers. */}
              {onCreateDraft && (isReady || isCreating) && (
                <>
                  <Textarea
                    value={customInstructions}
                    onChange={(event) => setCustomInstructions(event.target.value)}
                    placeholder="Optional instructions for draft generation"
                    rows={4}
                    disabled={isCreating}
                  />
                  <div className="flex flex-wrap gap-2">
                    {!isWordPressConnected && isReady && !isCreating ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span tabIndex={0} className="inline-block">
                            <Button disabled className="pointer-events-none">
                              Generate Post Draft
                            </Button>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          Draft generation is available only for domains with a
                          connected WordPress site
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <Button
                        onClick={() =>
                          onCreateDraft?.(
                            idea,
                            "posts",
                            customInstructions.trim() || undefined,
                          )
                        }
                        disabled={!isReady || isCreating}
                      >
                        {isCreating
                          ? "Draft generation in progress"
                          : "Generate Post Draft"}
                      </Button>
                    )}
                    {!isWordPressConnected && isReady && !isCreating ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span tabIndex={0} className="inline-block">
                            <Button
                              variant="outline"
                              disabled
                              className="pointer-events-none"
                            >
                              Generate Page Draft
                            </Button>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          Draft generation is available only for domains with a
                          connected WordPress site
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <Button
                        variant="outline"
                        onClick={() =>
                          onCreateDraft?.(
                            idea,
                            "pages",
                            customInstructions.trim() || undefined,
                          )
                        }
                        disabled={!isReady || isCreating}
                      >
                        {isCreating
                          ? "Draft generation in progress"
                          : "Generate Page Draft"}
                      </Button>
                    )}
                  </div>
                </>
              )}

              {isImplemented && (
                <div className="flex flex-wrap items-center gap-3">
                  {idea.implemented_page_id ? (
                    <Button
                      variant="outline"
                      onClick={() => onOpenLinkedPage?.(idea)}
                    >
                      {idea.implemented_page_type === "post"
                        ? "Open Implemented Post"
                        : idea.implemented_page_type === "page"
                          ? "Open Implemented Page"
                          : "View Existing Page"}
                    </Button>
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      Linked page unavailable
                    </div>
                  )}
                  {statusDescription && (
                    <div className="text-sm text-muted-foreground">
                      {statusDescription}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="grid gap-4 lg:grid-cols-[minmax(0,280px)_1fr]">
              <div className="space-y-4">
                <div className="rounded-lg border p-4 space-y-3">
                  <div className="text-sm font-medium">Idea Summary</div>
                  <div className="space-y-3">
                    {isEditing ? (
                      <>
                        <EditTextField
                          label="Title"
                          value={activeDraft.title}
                          onChange={(value) => updateDraft("title", value)}
                        />
                        <EditTextField
                          label="Target Keyword"
                          value={activeDraft.target_keyword}
                          onChange={(value) =>
                            updateDraft("target_keyword", value)
                          }
                        />
                        <EditTextField
                          label="Suggested Slug"
                          value={activeDraft.suggested_slug}
                          onChange={(value) =>
                            updateDraft("suggested_slug", value)
                          }
                          placeholder="example-page-slug"
                        />
                        <EditTextField
                          label="Target Audience"
                          value={activeDraft.target_audience}
                          onChange={(value) =>
                            updateDraft("target_audience", value)
                          }
                        />
                      </>
                    ) : (
                      <>
                        <DetailRow label="Title" value={idea.title} />
                        <DetailRow
                          label="Target Keyword"
                          value={idea.target_keyword}
                        />
                        <DetailRow
                          label="Suggested Slug"
                          value={
                            idea.suggested_slug
                              ? `/${idea.suggested_slug}`
                              : null
                          }
                        />
                        <DetailRow
                          label="Target Audience"
                          value={idea.target_audience}
                        />
                      </>
                    )}
                    <DetailRow label="Status" value={ideaUiState.label} />
                    <DetailRow
                      label="Implemented Page"
                      value={idea.implemented_page_id}
                    />
                    <DetailRow
                      label="Implemented Type"
                      value={idea.implemented_page_type}
                    />
                    <DetailRow
                      label="Implemented At"
                      value={implementedAt}
                    />
                    <DetailRow label="Created" value={createdAt} />
                    <DetailRow label="Updated" value={updatedAt} />
                    <DetailLinkRow
                      label="Origin"
                      href={conversationLinkFor(idea)}
                    >
                      Generated from a conversation
                    </DetailLinkRow>
                  </div>
                </div>
              </div>

              <div className="space-y-4 min-w-0">
                <div className="rounded-lg border p-4 space-y-3">
                  {isEditing ? (
                    <EditTextareaField
                      label="Description"
                      value={activeDraft.description}
                      onChange={(value) => updateDraft("description", value)}
                      placeholder="Describe the content idea"
                    />
                  ) : (
                    <>
                      <div className="text-sm font-medium">Description</div>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                        {idea.description || "No description available."}
                      </p>
                    </>
                  )}
                </div>

                <div className="rounded-lg border p-4 space-y-3">
                  {isEditing ? (
                    <EditTextareaField
                      label="Rationale"
                      value={activeDraft.rationale}
                      onChange={(value) => updateDraft("rationale", value)}
                      placeholder="Explain why this idea matters"
                    />
                  ) : (
                    <>
                      <div className="text-sm font-medium">Rationale</div>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                        {idea.rationale || "No rationale available."}
                      </p>
                    </>
                  )}
                </div>

                <div className="rounded-lg border p-4 space-y-3">
                  {isEditing ? (
                    <EditTextareaField
                      label="Source Input"
                      value={activeDraft.source_input}
                      onChange={(value) => updateDraft("source_input", value)}
                      placeholder="Source context used for this idea"
                    />
                  ) : (
                    <>
                      <div className="text-sm font-medium">Source Input</div>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                        {idea.source_input || "No source input available."}
                      </p>
                    </>
                  )}
                </div>

                <div className="rounded-lg border p-4 space-y-3">
                  {isEditing ? (
                    <EditTextareaField
                      label="Content Outline"
                      value={activeDraft.content_outline}
                      onChange={(value) => updateDraft("content_outline", value)}
                      placeholder="One outline item per line"
                      rows={8}
                    />
                  ) : (
                    <>
                      <div className="text-sm font-medium">Content Outline</div>
                      {hasOutline ? (
                        <ol className="list-decimal space-y-2 pl-5 text-sm text-muted-foreground">
                          {idea.content_outline.map((item, index) => (
                            <li key={`${idea.id}-outline-${index}`}>{item}</li>
                          ))}
                        </ol>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          No outline available.
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
